<script setup lang="ts">
/*
* Компонент NewTreeMultiSelectComponent - МНОЖЕСТВЕННЫЙ выбор в древовидной структуре
*
* Отличия от базовой версии:
* - Поддержка выбора нескольких узлов (чекбоксы)
* - Отсутствие автозакрытия при клике
* - Кнопка "Применить" для фиксации выбора
* - Входной/Выходной формат данных - массив ID строк
*/

// Импорты Vue
import { computed, onMounted, ref, watch } from 'vue';
import { VIconBtn } from 'vuetify/labs/VIconBtn';

import AnimatedLogoLoader from '@/components/createdUi/AnimatedLogoLoader.vue';
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore';

// Импорты типов
import type { ITreeNode } from '@/modules/apiStore/types/ITreeNode';

const props = defineProps({
  placeholder: { type: String, default: '' },
  persistentPlaceholder: { type: Boolean, default: true },
  searchPlaceholder: { type: String, default: '' },
  label: { type: String, default: '' },
  isLabelRequired: { type: Boolean, default: false },
  error: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  // ИЗМЕНЕНИЕ: Принимаем массив строк (ID)
  selected: {
    type: Array as PropType<string[]>,
    default: () => [],
  },
  treeData: {
    type: Array as PropType<ITreeNode[]>,
    required: true,
  },
  /*
   * Если передан — непустой запрос уходит на сервер, в списке ответ как есть.
   * Без пропа поиск остаётся локальным по treeData.
   */
  searchTree: {
    type: Function as PropType<(query: string) => Promise<ITreeNode[]>>,
    default: undefined,
  },
})

// ИЗМЕНЕНИЕ: Эмитим массив строк
const emit = defineEmits<{
  'update:selected': [value: string[]]
}>()

// Реактивные состояния компонента
const showDropdown = ref(false)
const selectedKeys = ref<string[]>([]) // Здесь храним ID выбранных элементов
const isSelectedMenuOpen = ref(false)
const expandedKeys = ref<string[]>([])
const search = ref('')
const searchDebounced = refDebounced(search, 300)
const feedbackStore = useFeedbackStore()

const serverTree = ref<ITreeNode[] | null>(null)
const searchLoading = ref(false)
let searchSeq = 0

const useServerSearch = computed(() => Boolean(props.searchTree) && searchDebounced.value.trim().length > 0)

const activeTree = computed(() => useServerSearch.value ? (serverTree.value ?? []) : props.treeData)

/**
 * Рекурсивный поиск узлов по массиву ключей
 * Необходим для получения данных (short_name/label) выбранных элементов
 */
function findNodesByKeys(nodes: ITreeNode[], keys: string[]): ITreeNode[] {
  const found: ITreeNode[] = []
  for (const node of nodes) {
    if (keys.includes(node.key)) {
      found.push(node)
    }
    if (node.children && node.children.length) {
      found.push(...findNodesByKeys(node.children, keys))
    }
  }
  return found
}

/**
 * Выбранные узлы (полные объекты)
 * Используются для отображения чипсов в инпуте
 */
const selectedNodes = computed(() => {
  return findNodesByKeys(props.treeData, props.selected)
})

const selectedItemsString = computed(() => {
  return selectedNodes.value.map(node => (node.data as any)?.short_name || node.label).join(', ')
})

/**
 * Вычисляемое свойство для определения состояния выбора каждого узла ('all' | 'partial' | 'none')
 */
const nodeSelectionStateMap = computed(() => {
  const map = new Map<string, 'all' | 'partial' | 'none'>()

  const check = (node: ITreeNode): 'all' | 'partial' | 'none' => {
    if (!node.children || node.children.length === 0) {
      const isSelected = selectedKeys.value.includes(node.key)
      const state = isSelected ? 'all' : 'none'
      map.set(node.key, state)
      return state
    }

    let allChildrenSelected = true
    let noneChildrenSelected = true

    for (const child of node.children) {
      const childState = check(child)
      if (childState !== 'all') {
        allChildrenSelected = false
      }
      if (childState !== 'none') {
        noneChildrenSelected = false
      }
    }

    let state: 'all' | 'partial' | 'none' = 'none'
    if (allChildrenSelected) {
      state = 'all'
    } else if (noneChildrenSelected) {
      state = selectedKeys.value.includes(node.key) ? 'all' : 'none'
    } else {
      state = 'partial'
    }

    map.set(node.key, state)
    return state
  }

  for (const node of props.treeData) {
    check(node)
  }

  return map
})

/**
 * Вычисляемое свойство для видимых узлов дерева
 */
const visibleNodes = computed(() => {
  const term = useServerSearch.value ? '' : searchDebounced.value
  return getVisibleNodes(activeTree.value, expandedKeys.value, term)
})

/**
 * Рекурсивная функция получения видимых узлов
 */
function getVisibleNodes(nodes: ITreeNode[], expandedKeys: string[], searchTerm = '', depth = 0): any[] {
  const visible: any[] = []

  nodes.forEach(node => {
    const isVisible = !searchTerm
      || node.label.toLowerCase().includes(searchTerm.toLowerCase())
      || isChildMatchingSearch(node, searchTerm)

    if (isVisible) {
      visible.push({
        ...node,
        depth,
        isLeaf: !node.children || node.children.length === 0,
      })

      const shouldShowChildren = expandedKeys.includes(node.key) || searchTerm
      if (shouldShowChildren && node.children) {
        visible.push(...getVisibleNodes(node.children, expandedKeys, searchTerm, depth + 1))
      }
    }
  })

  return visible
}

/**
 * Проверка соответствия дочерних узлов поисковому запросу
 */
function isChildMatchingSearch(node: ITreeNode, searchTerm: string): boolean {
  if (!node.children) return false
  return node.children.some(child =>
    child.label.toLowerCase().includes(searchTerm.toLowerCase())
    || isChildMatchingSearch(child, searchTerm),
  )
}

/**
 * Переключение состояния раскрытия узла
 */
function toggleNode(node: ITreeNode) {
  const isExpanded = expandedKeys.value.includes(node.key)
  if (isExpanded) {
    expandedKeys.value = expandedKeys.value.filter(key => key !== node.key)
  } else {
    expandedKeys.value = [...expandedKeys.value, node.key]
  }
}

/**
 * Получение всех дочерних ключей рекурсивно
 */
function getAllDescendantKeys(node: ITreeNode): string[] {
  const keys: string[] = []
  const collect = (n: ITreeNode) => {
    if (n.children) {
      for (const child of n.children) {
        keys.push(child.key)
        collect(child)
      }
    }
  }
  collect(node)
  return keys
}

/**
 * ИЗМЕНЕНИЕ: Обработчик клика по узлу (Чекбокс логика)
 * Назначение:
 * - Добавляет или убирает ID узла из массива selectedKeys (с поддержкой каскадности для родителей)
 * - НЕ закрывает модальное окно
 */
function findNodeByKey(nodes: ITreeNode[], key: string): ITreeNode | null {
  for (const node of nodes) {
    if (String(node.key) === String(key))
      return node
    if (node.children?.length) {
      const found = findNodeByKey(node.children, key)
      if (found)
        return found
    }
  }
  return null
}

function collectExpandableKeys(nodes: ITreeNode[]): string[] {
  const keys: string[] = []
  const walk = (list: ITreeNode[]) => {
    for (const node of list) {
      if (node.children?.length) {
        keys.push(node.key)
        walk(node.children)
      }
    }
  }
  walk(nodes)
  return keys
}

function handleNodeSelect(node: ITreeNode) {
  const source = findNodeByKey(props.treeData, node.key) ?? node
  const isLeaf = !source.children || source.children.length === 0

  if (isLeaf) {
    const index = selectedKeys.value.indexOf(source.key)
    if (index === -1) {
      selectedKeys.value.push(source.key)
    } else {
      selectedKeys.value.splice(index, 1)
    }
  } else {
    const descendantKeys = getAllDescendantKeys(source)
    const currentState = nodeSelectionStateMap.value.get(source.key) || 'none'

    if (currentState === 'all') {
      // Снимаем выбор с себя и со всех потомков
      selectedKeys.value = selectedKeys.value.filter(k => k !== source.key && !descendantKeys.includes(k))
    } else {
      // Выбираем себя и всех потомков
      const toAdd = [source.key, ...descendantKeys]
      for (const key of toAdd) {
        if (!selectedKeys.value.includes(key)) {
          selectedKeys.value.push(key)
        }
      }

      // Автоматически раскрываем этот узел и его подотделы
      const toExpand = [source.key, ...descendantKeys]
      for (const key of toExpand) {
        if (!expandedKeys.value.includes(key)) {
          expandedKeys.value.push(key)
        }
      }
    }
  }
}

/**
 * ИЗМЕНЕНИЕ: Применение выбора (по кнопке)
 */
function applySelection() {
  // Эмитим копию массива ключей
  emit('update:selected', [...selectedKeys.value])
  showDropdown.value = false
}

/**
 * Сброс выбора
 */
const clearSelection = () => {
  if (props.disabled) {
    return
  }
  resetSearch()
  selectedKeys.value = []
  emit('update:selected', [])
  showDropdown.value = false
}

/**
 * Сброс поискового запроса
 */
const resetSearch = () => {
  search.value = ''
}

/**
 * Подсветка совпадений
 */
const highlightMatch = (label: string, searchTerm: string) => {
  if (!searchTerm) return label
  const safe = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const re = new RegExp(`(${safe})`, 'gi')
  return (label ?? '').replace(re, '<mark class="search-highlight">$1</mark>')
}

/**
 * Раскрытие дерева при инициализации
 */
function expandTreeOnInit() {
  const newExpandedKeys: string[] = []
  for (const data of props.treeData) {
    newExpandedKeys.push(data.key)
  }
  expandedKeys.value = newExpandedKeys
}

/**
 * ИЗМЕНЕНИЕ: Восстановление выбора из props
 * Теперь просто копируем массив ID
 */
function restoreSelectionFromProp() {
  if (props.selected && Array.isArray(props.selected)) {
    selectedKeys.value = [...props.selected]
  } else {
    selectedKeys.value = []
  }
}

const selectedItemsData = computed(() => {
  return findNodesByKeys(props.treeData, selectedKeys.value).map(node => ({
    id: node.key,
    name: (node.data as any)?.short_name || node.label,
    description: '',
  }))
})

const removeKey = (key: string | number) => {
  const keyStr = String(key)
  selectedKeys.value = selectedKeys.value.filter(k => k !== keyStr)
  if (selectedKeys.value.length === 0) {
    isSelectedMenuOpen.value = false
  }
}

const removeAllKeys = () => {
  selectedKeys.value = []
  isSelectedMenuOpen.value = false
}

/**
 * Вотчеры
 */
watch(() => props.selected, () => {
  restoreSelectionFromProp()
}, { immediate: true, deep: true })

watch(
  () => props.treeData,
  newTree => {
    if (!newTree.length) return
    if (selectedKeys.value.length === 0) {
      restoreSelectionFromProp()
    }
  },
  { immediate: true },
)

onMounted(async () => {
  if (props.treeData.length) {
    expandTreeOnInit()
  }
})

watch(searchDebounced, async query => {
  if (!props.searchTree)
    return

  const trimmed = query.trim()
  if (!trimmed) {
    searchSeq += 1
    serverTree.value = null
    searchLoading.value = false
    expandTreeOnInit()
    return
  }

  const seq = ++searchSeq
  searchLoading.value = true
  try {
    const tree = await props.searchTree(trimmed)
    if (seq !== searchSeq)
      return
    serverTree.value = tree
    expandedKeys.value = collectExpandableKeys(tree)
  }
  catch {
    if (seq !== searchSeq)
      return
    serverTree.value = []
    feedbackStore.showToast({
      type: 'error',
      message: 'Не удалось выполнить поиск',
      time: 5000,
    })
  }
  finally {
    if (seq === searchSeq)
      searchLoading.value = false
  }
})
</script>

<template>
  <VMenu
    v-model="showDropdown"
    :close-on-content-click="false"
    :eager="false"
    :max-height="600"
    :disabled="disabled"
    location="bottom"
    transition="scale-transition"
    content-class="entity-form-menu departments-select-menu"
  >
    <template #activator="{ props: menuProps }">
      <VTextarea
        v-bind="menuProps"
        variant="outlined"
        density="comfortable"
        class="invisible-readonly"
        :persistent-placeholder="persistentPlaceholder"
        :placeholder="placeholder"
        :label="props.label"
        readonly
        :model-value="selectedItemsString"
        hide-details="auto"
        :error="error"
        :disabled="disabled"
        :clearable="!disabled"
        :append-inner-icon="showDropdown ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"
        rows="1"
        auto-grow
        no-resize
        @click:clear="clearSelection"
        @click:append-inner.stop="!disabled && (showDropdown = !showDropdown)"
      >
        <template v-if="props.label" #label>
          <span>{{ props.label }}<sup v-if="isLabelRequired" class="required-label">*</sup></span>
        </template>
      </VTextarea>
    </template>

    <VCard
      v-if="showDropdown"
      class="d-flex flex-column"
    >
      <VSheet class="pa-4">
        <VTextField
          v-model="search"
          variant="outlined"
          hide-details
          clearable
          rounded="pill"
          class="search-field"
          :placeholder="searchPlaceholder"
          :append-inner-icon="search ? undefined : 'ri-search-line'"
          @click:clear="resetSearch"
        />
      </VSheet>

      <!-- Оверлей выбранных элементов (при наведении) -->
      <div class="px-4 pb-2" :style="{ visibility: selectedKeys.length === 0 ? 'hidden' : 'visible' }">
        <VMenu
          v-model="isSelectedMenuOpen"
          open-on-hover
          :close-on-content-click="false"
          close-delay="200"
          location="bottom start"
          offset="8"
          content-class="date-picker-menu"
        >
          <template #activator="{ props: menuProps }">
            <span
              v-bind="menuProps"
              class="text-body-1 text-medium-emphasis cursor-pointer d-inline-block"
            >
              Выбрано: {{ selectedKeys.length }}
            </span>
          </template>

          <CustomSelectedItems
            :items="selectedItemsData"
            :title="`Выбрано ${selectedKeys.length}:`"
            hide-avatar
            @remove="removeKey"
            @remove-all="removeAllKeys"
          />
        </VMenu>
      </div>

      <div v-if="searchLoading" class="tree-search-loader d-flex align-center justify-center">
        <AnimatedLogoLoader />
      </div>
      <div v-else-if="useServerSearch && !visibleNodes.length" class="text-body-2 text-medium-emphasis px-4">
        Ничего не найдено
      </div>
      <VList v-else class="tree-view-content custom-tree px-2">
        <div
          v-for="item in visibleNodes"
          :key="item.key"
          class="tree-item"
          :class="{
            'tree-item--selected': selectedKeys.includes(item.key),
            'tree-item--leaf': item.isLeaf,
          }"
          :style="{ paddingLeft: `${item.depth * 34 + 8}px` }"
          @click="handleNodeSelect(item)"
        >
          <VIconBtn
            v-if="!item.isLeaf"
            icon="ri-play-fill"
            class="expand-btn"
            variant="text"
            :class="{ expanded: expandedKeys.includes(item.key) }"
            @click.stop="toggleNode(item)"
          />
          <div v-else class="node-leaf-spacer" />

          <span
            class="text-body-1 text-high-emphasis tree-item__label"
            v-html="highlightMatch(item.label, searchDebounced)"
          />
        </div>
      </VList>

      <VDivider />

      <div class="select-menu-footer">
        <VBtn
          class="w-100 w-sm-auto"
          variant="text"
          color="secondary"
          @click="showDropdown = false"
        >
          Отмена
        </VBtn>
        <VBtn
          class="w-100 w-sm-auto"
          color="primary"
          variant="elevated"
          @click="applySelection"
        >
          Применить
        </VBtn>
      </div>
    </VCard>
  </VMenu>
</template>

<style scoped lang="scss">
:global(.departments-select-menu) {
  max-width: 0 !important;
}

.tree-search-loader {
  min-height: 160px;
}

.tree-view-content {
  max-height: 300px;
  overflow-y: auto;
  position: relative;
  flex-grow: 1;
  min-height: 0;

  @media (max-width: 600px) {
    max-height: none !important;
  }
}

:deep(.search-highlight) {
  background-color: rgb(var(--v-theme-primary-500), var(--v-activated-opacity));
  padding: 2px 0;
  border-radius: 2px;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
}

:deep(.v-theme--dark .search-highlight) {
  background-color: rgba(var(--v-theme-primary-500), 0.38);
  padding: 2px 0;
  border-radius: 2px;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
}
</style>
